/**
 * GroupsView — Search's By Type and By Arche summaries, shaped like email's
 * By Sender: one card per group with its file count, total size and newest
 * file; clicking a card opens the Standard list narrowed to that group.
 *
 * The numbers come from the server over EVERY matching file (not the loaded
 * page) and from the same WHERE as the list, so a card's count is what its
 * list shows. Files above the viewer's sensitivity tier are counted nowhere
 * here — the Storage panel reports them as one restricted total.
 */
import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronRight, Loader2, PackageOpen, Upload } from 'lucide-react';
import type { VaultDocumentArcheGroupsDto, VaultDocumentKindGroupsDto } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatModified, iconForMime } from '@/components/files/file-dialog/format';
import { VAULT_FILE_KIND_LABELS, type VaultFileKind } from '@/lib/vault-file-kinds';
import { archeLabel, noArcheLabel } from '../../lib/arche-labels';
import { isImageMime } from '../../lib/file-display';
import { DocumentThumbnail } from '../shared/DocumentThumbnail';
import { describeTotals, type SearchDrill } from './search-filters';

type SortKey = 'name' | 'count' | 'size' | 'newest';
type SortDir = 'asc' | 'desc';
interface GroupSort {
  key: SortKey;
  dir: SortDir;
}

const SORT_STORAGE_KEY = 'fias.vault.search.groupSort';
const DEFAULT_SORT: GroupSort = { key: 'count', dir: 'desc' };

function loadSort(): GroupSort {
  try {
    const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<GroupSort>;
      const keys: SortKey[] = ['name', 'count', 'size', 'newest'];
      return {
        key: keys.includes(p.key as SortKey) ? (p.key as SortKey) : DEFAULT_SORT.key,
        dir: p.dir === 'asc' ? 'asc' : 'desc',
      };
    }
  } catch {
    // unavailable or malformed storage — the default is fine
  }
  return DEFAULT_SORT;
}

function saveSort(sort: GroupSort): void {
  try {
    window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
  } catch {
    // a per-browser convenience only
  }
}

export interface GroupCard {
  id: string;
  label: string;
  icon: React.ReactNode;
  count: number;
  totalBytes: number;
  newestAt: string | null;
  sample: VaultDocumentKindGroupsDto['groups'][number]['sample'];
  drill: SearchDrill;
}

export function kindCards(data: VaultDocumentKindGroupsDto): GroupCard[] {
  return data.groups.map((g) => {
    const kind = g.kind as VaultFileKind;
    const Icon = iconForMime(g.sample[0]?.mimeType ?? null);
    return {
      id: kind,
      label: VAULT_FILE_KIND_LABELS[kind],
      icon: <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />,
      count: g.count,
      totalBytes: g.totalBytes,
      newestAt: g.newestAt,
      sample: g.sample,
      drill: { by: 'kind', kind, label: VAULT_FILE_KIND_LABELS[kind] },
    };
  });
}

export function archeCards(data: VaultDocumentArcheGroupsDto): GroupCard[] {
  return data.groups.map((g) => {
    const label =
      g.group === 'arche'
        ? archeLabel({ archeId: g.archeId, archeName: g.archeName }, 'arche-saves')
        : g.group === 'fias'
          ? noArcheLabel('arche-saves')
          : noArcheLabel('documents');
    return {
      id: g.key,
      label,
      icon:
        g.group === 'uploads' ? (
          <Upload className="h-5 w-5 text-muted-foreground" aria-hidden />
        ) : (
          <PackageOpen className="h-5 w-5 text-primary" aria-hidden />
        ),
      count: g.count,
      totalBytes: g.totalBytes,
      newestAt: g.newestAt,
      sample: g.sample,
      drill: { by: 'arche', group: g.group, archeId: g.archeId, label },
    };
  });
}

function SortChip({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent',
      )}
    >
      {label}
      {active &&
        (dir === 'asc' ? (
          <ArrowUp className="h-3 w-3" aria-label="ascending" />
        ) : (
          <ArrowDown className="h-3 w-3" aria-label="descending" />
        ))}
    </button>
  );
}

export function GroupsView({
  cards,
  loading,
  error,
  other,
  onOpen,
  emptyMessage,
}: {
  cards: readonly GroupCard[];
  loading: boolean;
  error: boolean;
  /** Groups past the server's cap, rolled up. */
  other?: { count: number; totalBytes: number; groupCount: number } | null;
  onOpen: (drill: SearchDrill) => void;
  emptyMessage: string;
}) {
  const [sort, setSort] = useState<GroupSort>(loadSort);
  const sorted = useMemo(() => {
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...cards].sort((a, b) => {
      let diff = 0;
      if (sort.key === 'name')
        diff = a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      else if (sort.key === 'count') diff = a.count - b.count;
      else if (sort.key === 'size') diff = a.totalBytes - b.totalBytes;
      else diff = (a.newestAt ?? '').localeCompare(b.newestAt ?? '');
      return diff * factor || a.id.localeCompare(b.id);
    });
  }, [cards, sort]);
  const choose = (key: SortKey) => {
    const next: GroupSort =
      sort.key === key
        ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' };
    setSort(next);
    saveSort(next);
  };

  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Adding it up…
      </p>
    );
  }
  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Couldn’t load the summary. Try again.
      </p>
    );
  }
  if (cards.length === 0) {
    return (
      <div className="rounded border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-1.5" aria-label="Sort groups">
        <span className="text-xs text-muted-foreground">Sort</span>
        <SortChip
          label="Name"
          active={sort.key === 'name'}
          dir={sort.dir}
          onClick={() => choose('name')}
        />
        <SortChip
          label="Files"
          active={sort.key === 'count'}
          dir={sort.dir}
          onClick={() => choose('count')}
        />
        <SortChip
          label="Size"
          active={sort.key === 'size'}
          dir={sort.dir}
          onClick={() => choose('size')}
        />
        <SortChip
          label="Newest"
          active={sort.key === 'newest'}
          dir={sort.dir}
          onClick={() => choose('newest')}
        />
      </div>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {sorted.map((card) => (
          <li key={card.id}>
            <button
              type="button"
              onClick={() => onOpen(card.drill)}
              className="group flex w-full flex-col gap-2 rounded-lg border border-border bg-card p-3 text-left transition hover:border-primary/40 hover:shadow-sm"
            >
              <div className="flex items-center gap-2">
                {card.icon}
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                  {card.label}
                </span>
                <ChevronRight
                  className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5"
                  aria-hidden
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {describeTotals(card.count, card.totalBytes)}
                {card.newestAt && ` · newest ${formatModified(card.newestAt)}`}
              </p>
              {card.sample.length > 0 && (
                <div className="flex items-center gap-1.5 overflow-hidden" aria-hidden>
                  {card.sample.slice(0, 5).map((s) => {
                    const Icon = iconForMime(s.mimeType);
                    return (
                      <span
                        key={s.documentId}
                        title={s.name}
                        className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded bg-muted/60"
                      >
                        {isImageMime(s.mimeType) ? (
                          <DocumentThumbnail
                            documentId={s.documentId}
                            name={s.name}
                            iconClassName="h-4 w-4"
                          />
                        ) : (
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        )}
                      </span>
                    );
                  })}
                  {card.count > 5 && (
                    <span className="text-xs text-muted-foreground">
                      +{(card.count - 5).toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </button>
          </li>
        ))}
      </ul>
      {other && other.groupCount > 0 && (
        <p className="text-xs text-muted-foreground">
          And {other.groupCount.toLocaleString()} more {other.groupCount === 1 ? 'arche' : 'arches'}{' '}
          — {describeTotals(other.count, other.totalBytes)}. Narrow the filters to see them.
        </p>
      )}
    </div>
  );
}
